import { Divider, Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { FlowRestResponseBodyFormat } from 'flow-schema';
import { startCase } from 'lodash';

import { Text, api, getQueryProps, isDefined } from 'common';

import { useFlowDefinitionContext } from '../../../../context';
import {
  BlockValueTemplateField,
  BlockValueTemplateFieldArea,
} from '../../ConfigFields/CodeMirror/FlowTemplate';
import { BlockKeyValuesField } from '../../ConfigFields/KeyValues/KeyValuesField';
import { BlockSelectField } from '../../ConfigFields/SelectField';
import { useSelectedNodeField } from '../../ConfigFields/useSelectedNodeField';

const ApiInstanceFhirMethod = api.Orchestrator.ApiInstanceFhir.ApiInstanceFhirMethod;

type MontageOnBaseBlockConfig = api.FlowDefinition.MontageOnBaseBlock['config'];

export function MontageOnBaseFormFields() {
  const {
    flowDefinition: { organizationId },
  } = useFlowDefinitionContext();

  const { data: apiInstances = [] } = useQuery({
    ...getQueryProps(api.client.Orchestrator.ApiInstance, 'findAllBasic', { organizationId }),
  });

  const { value: apiInstanceId } =
    useSelectedNodeField<MontageOnBaseBlockConfig['apiInstanceId']>('config.apiInstanceId');

  const { value: apiInstanceBaseUrl, setValue: setApiInstanceBaseUrl } = useSelectedNodeField<
    MontageOnBaseBlockConfig['apiInstanceBaseUrl']
  >('config.apiInstanceBaseUrl');

  const { value: methodValue } =
    useSelectedNodeField<MontageOnBaseBlockConfig['method']>('config.method');

  const { value: endpointValue } =
    useSelectedNodeField<MontageOnBaseBlockConfig['endpoint']>('config.endpoint');

  const selectedApiInstance = apiInstances.find((a) => a.id === apiInstanceId);
  const baseUrl = selectedApiInstance?.baseUrl ?? apiInstanceBaseUrl ?? '';

  const normalizedPath =
    isDefined(endpointValue) && endpointValue.length > 0
      ? endpointValue.startsWith('/')
        ? endpointValue
        : `/${endpointValue}`
      : '';
  const composedUrl = baseUrl.length > 0 ? `${baseUrl.replace(/\/$/, '')}${normalizedPath}` : null;

  return (
    <Stack spacing={2}>
      <BlockSelectField
        label="API Instance"
        fieldName="config.apiInstanceId"
        required={true}
        options={apiInstances.map((apiInstance) => ({
          label: apiInstance.name,
          value: apiInstance.id,
        }))}
        onChange={(event) => {
          const instance = apiInstances.find((a) => a.id === event.target.value);
          setApiInstanceBaseUrl(instance?.baseUrl ?? undefined);
        }}
        onClear={() => {
          setApiInstanceBaseUrl(undefined);
        }}
      />
      <Stack direction="row" spacing={1}>
        <BlockSelectField
          label="Method"
          fieldName="config.method"
          required
          options={Object.values(ApiInstanceFhirMethod).map((method) => ({
            label: method,
            value: method,
          }))}
        />
        <BlockValueTemplateField
          fieldName="config.endpoint"
          label="Endpoint"
          required
          isMultiline
          helperText={
            composedUrl ?? (baseUrl.length > 0 ? baseUrl : 'Select an API Instance first')
          }
        />
      </Stack>
      {isDefined(composedUrl) && (
        <Text variant="subtitle2" sx={{ color: 'text.secondary', wordBreak: 'break-all' }}>
          {composedUrl}
        </Text>
      )}
      <BlockKeyValuesField
        fieldName="config.headers"
        addRowHelperText="header"
        label="Headers"
        helperText="Additional request headers. Authorization is injected automatically."
      />
      <BlockSelectField
        fieldName="config.responseBodyFormat"
        label="Response Body Format"
        options={Object.values(FlowRestResponseBodyFormat).map((format) => ({
          label: startCase(format),
          value: format,
        }))}
        defaultValue={FlowRestResponseBodyFormat.AutoDetect}
      />
      {isDefined(methodValue) &&
        [
          ApiInstanceFhirMethod.POST,
          ApiInstanceFhirMethod.PUT,
          ApiInstanceFhirMethod.PATCH,
        ].includes(methodValue) && (
          <>
            <Divider />
            <BlockValueTemplateFieldArea
              fieldName="config.body"
              label="Body"
              helperText="JSON request body."
            />
          </>
        )}
    </Stack>
  );
}
